import React from 'react';
import ReactDOM from 'react-dom/client';

import {
	WayfindingComponent,
} from '@touchsource/wayfinding_lib';

import {
	buildList,
	clearListItemSelection
} from './mobile.js';

import { 
	envENV
} from './env';

import { getTLCUUID } from './mobile';

// Create Maps for better listing building
const setActiveMap = () => {
	const pendingUpdates = window.flatData;
	const activeSystem = `system.${window.systemUUID}`;

	let activeMap = '';
	let activeSystemPoint = '';
	let activeMapPointGroup = '';

	Object.entries(pendingUpdates).forEach(([m1Key, m1Data,]) => {
		if (m1Key.startsWith(activeSystem)) {
			Object.keys(m1Data).forEach((m2Key) => {
				if (m2Key.startsWith('wf_point.')) {
					activeSystemPoint = m2Key;
				}
			});
		}
	});
	
	Object.entries(pendingUpdates).forEach(([m1Key, m1Data,]) => {
		if (m1Key.startsWith('wf_point_group.')) {
			Object.keys(m1Data).forEach((m2Key) => {
				if (m2Key === activeSystemPoint) {
					activeMapPointGroup = m1Key;
				}
			});
		}
	});

	Object.entries(pendingUpdates).forEach(([m1Key, m1Data,]) => {
		if (m1Key.startsWith('wf_map_graphic.')) {
			Object.keys(m1Data).forEach((m2Key) => {
				if (m2Key === activeMapPointGroup) {
					activeMap = m1Key;
				}
			});
		}
	});

	window.activeMap = activeMap;
};

const getParentKeyNameFromTarget = (targetName, matchingValue) => {
	const flatData = window.flatData;
	let parentName = '';
	for (let m1KeyName of Object.keys(flatData)) {
		if (m1KeyName.startsWith(targetName)) {
			for (let m2KeyName of Object.keys(flatData[m1KeyName])) {
				if (m2KeyName === matchingValue) {
					parentName = m1KeyName;
					break;
				}
			}

			if (parentName) {
				break;
			}
		}
	}

	return parentName;
};

const getAccount = (fd = {}) => {
	if (!window.activeAccountM1Key) {
		let keys = Object.keys(fd);
		window.activeAccountM1Key = keys.find((key) => {
			return key.startsWith('account.');
		});
	}

	return fd[window.activeAccountM1Key] || {};
};

export const getSystemOrPropertyOrAccountSetting = (mapSetting, systemName = null, checkbox = false) => {
	const activeMap = window.activeMap;
	let settingValue = '';
	const pendingUpdates = window.flatData;

	const account = getAccount(pendingUpdates);
	const activeSystemGroup = pendingUpdates?.[getParentKeyNameFromTarget('system_group', activeMap)] || {};
	const activeSystem = pendingUpdates?.[systemName] || {};

	let accountSetting;
	if (mapSetting in account) {
		accountSetting = {
			value: account[mapSetting] ?? null,
		};
	}

	let propertySetting;
	if (mapSetting in activeSystemGroup) {
		propertySetting = {
			value: activeSystemGroup[mapSetting] ?? null,
		};
	}

	let systemSetting;
	if (mapSetting in activeSystem) {
		systemSetting = {
			value: activeSystem[mapSetting] ?? null,
		};
	}	

	if ((systemSetting?.value && !checkbox) || (checkbox && systemSetting?.value && systemSetting?.value !== false)) {
		settingValue = systemSetting.value;
	} else if ((propertySetting?.value && !checkbox) || (checkbox && propertySetting?.value && propertySetting?.value !== false)) {
		settingValue = propertySetting.value;
	} else if ((accountSetting?.value && !checkbox) || (checkbox && accountSetting?.value && accountSetting?.value !== false)) {
		settingValue = accountSetting.value;
	} else if (checkbox) {
		settingValue = false;
	} else {
		settingValue = '';
	}

	return settingValue;
};

export const wayfindingRef = React.createRef();

const getListingWayfindingData = (listingUUID) => {
	const flatData = window.flatData;

	let primaryWayfindingColumn = '';
	let secondaryWayfindingColumn = '';

	for (let [m1Key, m1Data] of Object.entries(flatData)) {
		if (m1Key.startsWith('listing_group.')) {
			for (let [m2Key, m2Data] of Object.entries(m1Data)) {
				if (m2Key.startsWith('listing_config.')) {
					if (m2Data.wf_column && flatData[listingUUID][`listing_column.${m2Data.wf_column}`]) {
						primaryWayfindingColumn = m2Data.wf_column;
					}
					if (m2Data.wf_fallback_column && flatData[listingUUID][`listing_column.${m2Data.wf_column}`]) {
						secondaryWayfindingColumn = m2Data.wf_fallback_column;
					}
				}
			}
		}
	}

	const landmarkIdentifier = flatData[listingUUID][`listing_column.${primaryWayfindingColumn}`]?.string;
	const landmarkIdentifierFallback = flatData[listingUUID][`listing_column.${secondaryWayfindingColumn}`]?.string;

	let legacyBuildingName = '';
	let legacyLandmarkId = '';
	let legacyLocationName = '';

	let listingConfigUUID = '';

	for (let [m1Key, m1Data] of Object.entries(flatData[listingUUID])) {
		if (m1Key.startsWith('listing_config.')) {
			listingConfigUUID = m1Key;
		}

		if (m1Key.startsWith('listing_column.')) {
			for (let [m2Key, ] of Object.entries(flatData[m1Key])) {
				if (m2Key.startsWith('listing_column_type.')) {
					if (flatData[m2Key].display_name === 'Building') {
						legacyBuildingName = m1Data.string;
					} else if (flatData[m2Key].display_name === 'Location (apt/suite)') {
						if (flatData[m1Key].display_name === 'landmark_id') {
							legacyLandmarkId = m1Data.string;
						} else {
							legacyLocationName = m1Data.string;
						}
					}
				}
			}
		}
	}

	return {
		legacyBuildingName,
		legacyLandmarkId,
		legacyLocationName,
		listingUUID,
		listingConfigUUID,
		landmarkIdentifier,
		landmarkIdentifierFallback,
		primaryWayfindingColumn,
		secondaryWayfindingColumn
	};
}

export const renderPath = (listingUUID) => {
	if (!wayfindingRef.current) {
		return;
	}

	wayfindingRef.current.setListingsAndGetLocation(getListingWayfindingData(listingUUID));
};

export const resetMapIfNoPath = () => {
	if (wayfindingRef.current === null) {
		return;
	}

	wayfindingRef.current.resetMapIfNoPath();
	setTimeout(() => {
		wayfindingRef.current.reloadImage();
	});
};

export const resetMap = () => {
	if (wayfindingRef.current === null) {
		return;
	}

	wayfindingRef.current.resetToInitialMap();
};

export const clearWayfindingMessage = () => {
	if (wayfindingRef.current === null) {
		return;
	}

	wayfindingRef.current.clearMessage();
};

export const setUseNearbyLocation = (keyValue) => {
	if (wayfindingRef.current === null) {
		return;
	}

	wayfindingRef.current.setUseNearbyLocation(keyValue);
};

// used in html
window.replay = () => {
	if (wayfindingRef.current === null) {
		return;
	}

	wayfindingRef.current.reloadImage();
};

export const wayfindingMapStartup = (listingUUID = null) => {
	let landmarkIdentifier = null;
	let landmarkIdentifierFallback = null;
	let legacyBuildingName = null;
	let legacyLandmarkId = null;
	let legacyLocationName = null;
	let listingConfigUUID = null;

	if (listingUUID) {
		const listingWayfindingData = getListingWayfindingData(listingUUID);
		landmarkIdentifier = listingWayfindingData.landmarkIdentifier;
		landmarkIdentifierFallback = listingWayfindingData.landmarkIdentifierFallback;
		legacyBuildingName = listingWayfindingData.legacyBuildingName;
		legacyLandmarkId = listingWayfindingData.legacyLandmarkId;
		legacyLocationName = listingWayfindingData.legacyLocationName;
		listingConfigUUID = listingWayfindingData.listingConfigUUID;
	}

    setActiveMap();

    const systemData = window.flatData[`system.${window.systemUUID}`];
	let wayfindingProject = false;

	for (const m1Key of Object.keys(systemData)) {
		if (m1Key.startsWith('wf_point.')) {
			wayfindingProject = true;
		}
	}

	if (!wayfindingProject) {
        return false;
    }

    const body = document.getElementsByTagName('body')[0];
    body.classList.add('hasWayfinding');

    const systemUUID = window.systemUUID;
    const flatData = window.flatData;
    const activeMap = window.activeMap;

    const url = new URL(window.location.href);
    const componentUUID = getTLCUUID();
    const env = url.searchParams.get('wf_env') || envENV;
    
    const activeSystemGroup = getParentKeyNameFromTarget('system_group', activeMap) || '';
    
    const locationMarkerUse = getSystemOrPropertyOrAccountSetting('wf_location_marker_use', `system.${systemUUID}`, true);
	window.locationMarkerUse = locationMarkerUse;
    const locationMarkerColor = getSystemOrPropertyOrAccountSetting('wf_location_marker_color', `system.${systemUUID}`, true);
    const locationMarkerSize = getSystemOrPropertyOrAccountSetting('wf_location_marker_size', `system.${systemUUID}`, true);
    const mapDelayIn = getSystemOrPropertyOrAccountSetting('wf_map_delay_in', `system.${systemUUID}`, true);
    const mapDelayOut = getSystemOrPropertyOrAccountSetting('wf_map_delay_out', `system.${systemUUID}`, true);
    const pathColor = getSystemOrPropertyOrAccountSetting('wf_path_color', `system.${systemUUID}`, true);
    const pathEndmarker = getSystemOrPropertyOrAccountSetting('wf_path_endmarker', `system.${systemUUID}`, true);
    const pathEndmarkerSize = getSystemOrPropertyOrAccountSetting('wf_path_endmarker_size', `system.${systemUUID}`, true);
    const pathWidth = getSystemOrPropertyOrAccountSetting('wf_path_width', `system.${systemUUID}`, true);
	const showLabels = window?.newData?.mobileSettings?.show_labels || null;
	const showNearbyLocations = window?.newData?.mobileSettings?.show_nearby_locations || null;
    
    const wayfindingMapContainerElement = document.getElementById('wayfindingModal');
    
    const wayfindingRoot = ReactDOM.createRoot(wayfindingMapContainerElement);

    wayfindingRoot.render(
		<WayfindingComponent
			buildList={buildList}
			componentUUID={componentUUID}
			clearListItemSelection={clearListItemSelection}
			css={{}}
			env={env}
			flatData={flatData}
			hideQRCode={true}
			isMobile={true}
			landmarkIdentifier={landmarkIdentifier}
			landmarkIdentifierFallback={landmarkIdentifierFallback}
			legacyBuildingName={legacyBuildingName}
			legacyLandmarkId={legacyLandmarkId}
			legacyLocationName={legacyLocationName}
			listingConfigUUID={listingConfigUUID}
			listingUUID={listingUUID}
			locationMarkerUse={locationMarkerUse}
			locationMarkerColor={locationMarkerColor}
			locationMarkerSize={locationMarkerSize}
			mapDelayIn={mapDelayIn}
			mapDelayOut={mapDelayOut}
			pathColor={pathColor}
			pathEndmarker={pathEndmarker}
			pathEndmarkerSize={pathEndmarkerSize}
			pathWidth={pathWidth}
			qrCodeText={''}
			ref={wayfindingRef}
			setToInitialMap={listingUUID ? false : true}
			showLabels={showLabels}
			showNearbyLocations={showNearbyLocations}
			systemUUID={systemUUID}
			systemGroupUUID={activeSystemGroup}
		/>
    );
};